/** Casca da aplicação: barra lateral no desktop, barra inferior no celular. */

import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthContext';
import { AvisoDaAssinatura } from './AvisoDaAssinatura';
import { BotaoApresentacao, FaixaApresentacao } from './BotaoApresentacao';
import { Brasao, ehPlataforma, nomeDaLoja, useMarcaDaLoja } from './Marca';
import { Icone, type NomeIcone } from './Icone';
import { Carregando } from './base';
import { precarregar } from '../rotas';
import { iniciais } from '../utils/formato';

// O perfil (com a ficha inteira) só baixa quando o irmão abre os próprios dados.
const PerfilMembro = lazy(() => import('./PerfilMembro').then((m) => ({ default: m.PerfilMembro })));

interface ItemNav {
  para: string;
  icone: NomeIcone;
  rotulo: string;
  permissao: string;
  secao: string;
  /** Some quando o usuário tem esta permissão (há um item que já cobre o mesmo). */
  excetoCom?: string;
}

// Uma seção por cargo: cada perfil vê a parte comum (Loja, Quadro) e a área do
// próprio cargo. Antes havia "Restrito · Adm/Mestre" com Atas e Trabalhos, que
// todo irmão abre, e uma "Gestão" que para o irmão comum só tinha o GOB.
export const ITENS: ItemNav[] = [
  { para: '/', icone: 'inicio', rotulo: 'Visão geral', permissao: 'dashboard:ler', secao: '' },
  { para: '/calendario', icone: 'calendario', rotulo: 'Calendário', permissao: 'calendario:ler', secao: 'Loja' },
  { para: '/atas', icone: 'arquivo', rotulo: 'Atas', permissao: 'atas:ler', secao: 'Loja' },
  { para: '/trabalhos', icone: 'arquivoPena', rotulo: 'Trabalhos', permissao: 'trabalhos:ler', secao: 'Loja' },
  { para: '/fotos', icone: 'imagem', rotulo: 'Fotos', permissao: 'fotos:ler', secao: 'Loja' },
  { para: '/gob', icone: 'livro', rotulo: 'Documentos GOB', permissao: 'gob:ler', secao: 'Loja' },
  // Quem abre o Arquivo inteiro já tem a própria pasta lá dentro.
  {
    para: '/meus-documentos',
    icone: 'pastaAberta',
    rotulo: 'Meus documentos',
    permissao: 'arquivo:proprio',
    secao: 'Loja',
    excetoCom: 'arquivo:ler',
  },
  { para: '/arquivo', icone: 'pasta', rotulo: 'Arquivo', permissao: 'arquivo:ler', secao: 'Loja' },
  { para: '/irmaos', icone: 'pessoas', rotulo: 'Irmãos', permissao: 'quadro:ler', secao: 'Quadro' },
  { para: '/graduacoes', icone: 'graduacao', rotulo: 'Graduações', permissao: 'quadro:ler', secao: 'Quadro' },
  { para: '/projecao', icone: 'tendencia', rotulo: 'Projeção', permissao: 'quadro:ler', secao: 'Quadro' },
  { para: '/relatorios', icone: 'barras', rotulo: 'Relatórios', permissao: 'quadro:ler', secao: 'Quadro' },
  { para: '/chancelaria/sessoes', icone: 'pena', rotulo: 'Sessões', permissao: 'sessoes:ler', secao: 'Chancelaria' },
  { para: '/presencas', icone: 'presenca', rotulo: 'Presenças', permissao: 'sessoes:ler', secao: 'Chancelaria' },
  // As datas que o chanceler anuncia em sessão; pede a permissão de quem registra.
  { para: '/chancelaria/datas', icone: 'bolo', rotulo: 'Datas', permissao: 'sessoes:escrever', secao: 'Chancelaria' },
  // Todo irmão registra as próprias visitas (a Chancelaria vê as de todos na mesma tela).
  { para: '/visitacoes', icone: 'local', rotulo: 'Visitações', permissao: 'visitas:proprio', secao: 'Chancelaria' },
  { para: '/tesouraria', icone: 'carteira', rotulo: 'Mensalidades', permissao: 'tesouraria:ler', secao: 'Tesouraria' },
  { para: '/financeiro', icone: 'fluxo', rotulo: 'Financeiro', permissao: 'financeiro:resumo', secao: 'Tesouraria' },
  { para: '/orcamento', icone: 'pizza', rotulo: 'Orçamento', permissao: 'orcamento:ler', secao: 'Tesouraria' },
  { para: '/eventos', icone: 'estrela', rotulo: 'Eventos', permissao: 'eventos:ler', secao: 'Tesouraria' },
  { para: '/notas-fiscais', icone: 'recibo', rotulo: 'Notas fiscais', permissao: 'notas:ler', secao: 'Tesouraria' },
  { para: '/fornecedores', icone: 'predio', rotulo: 'Fornecedores', permissao: 'fornecedores:ler', secao: 'Tesouraria' },
  { para: '/contas-bancarias', icone: 'banco', rotulo: 'Contas e caixas', permissao: 'financeiro:ler', secao: 'Tesouraria' },
  { para: '/plano-contas', icone: 'arvore', rotulo: 'Plano de contas', permissao: 'financeiro:ler', secao: 'Tesouraria' },
  // Nome e brasão da loja: o que todo irmão vê em toda tela. Só o Administrador.
  { para: '/comunicados', icone: 'enviar', rotulo: 'Comunicados', permissao: 'comunicados:ler', secao: 'Administração' },
  { para: '/loja', icone: 'marca', rotulo: 'Identidade da loja', permissao: 'loja:escrever', secao: 'Administração' },
  { para: '/auditoria', icone: 'escudo', rotulo: 'Auditoria', permissao: 'auditoria:ler', secao: 'Administração' },
  { para: '/plataforma/lojas', icone: 'globo', rotulo: 'Lojas', permissao: 'plataforma:administrar', secao: 'Plataforma' },
  { para: '/plataforma/cobranca', icone: 'recibo', rotulo: 'Cobrança', permissao: 'plataforma:administrar', secao: 'Plataforma' },
];

//: Barra de baixo no celular: as primeiras quatro desta lista que o perfil
//: alcança. Fixas, o irmão comum ficava com duas (Tesouraria e Financeiro
//: não eram dele); assim o tesoureiro tem o caixa e o irmão, a agenda.
const PRIORIDADE_MOBILE = ['/', '/tesouraria', '/financeiro', '/calendario', '/irmaos'];

export function Layout() {
  const { usuario, sair, pode } = useAuth();
  const navegar = useNavigate();
  const [maisAberto, setMaisAberto] = useState(false);
  const [meusDados, setMeusDados] = useState(false);
  const meuMembro = usuario?.membro_id ?? null;
  const { pathname } = useLocation();
  const clienteQuery = useQueryClient();
  const preparar = (destino: string) => precarregar(destino, clienteQuery);

  // Em tela baixa o menu rola; o item da tela atual não pode ficar escondido
  // lá embaixo, senão some a referência de onde se está.
  useEffect(() => {
    document.querySelector('.sidebar .nav-item.active')?.scrollIntoView({ block: 'nearest' });
  }, [pathname]);

  // Os dados da tela atual saem já, em paralelo com o código dela — sem isto
  // a busca só começava depois de o pedaço da página ser baixado e montado.
  useEffect(() => {
    precarregar(pathname, clienteQuery);
  }, [pathname, clienteQuery]);

  const permitidos = ITENS.filter(
    (item) => pode(item.permissao) && !(item.excetoCom && pode(item.excetoCom)),
  );
  const secoes = [...new Set(permitidos.map((i) => i.secao))];
  // Quem lança a presença tem Sessões à mão: escondida no "Mais", a tarefa
  // que o Chanceler faz toda semana ficava a três toques do Início.
  const prioridade = pode('sessoes:escrever')
    ? ['/', '/chancelaria/sessoes', ...PRIORIDADE_MOBILE.slice(1)]
    : PRIORIDADE_MOBILE;
  const atalhos = prioridade.filter((p) => permitidos.some((i) => i.para === p)).slice(0, 4);
  const principais = atalhos.map((p) => permitidos.find((i) => i.para === p)!);
  const secundarios = permitidos.filter((i) => !atalhos.includes(i.para));
  const secoesSecundarias = [...new Set(secundarios.map((i) => i.secao))];

  const encerrar = async () => {
    await sair();
    navegar('/login', { replace: true });
  };

  const irPara = (destino: string) => {
    setMaisAberto(false);
    navegar(destino);
  };

  const loja = usuario?.loja;
  // Nome na aba e brasão no ícone do navegador — a marca não para na interface.
  useMarcaDaLoja(loja);

  return (
    <>
      <header id="mobile-header">
        <Brasao loja={loja} caixa="sid-gem" tamanho={18} />
        <div style={{ minWidth: 0 }}>
          <div className="mh-title">{nomeDaLoja(loja)}</div>
          <div className="mh-sub">{usuario?.nome_completo}</div>
        </div>
      </header>

      <div className="app">
        <nav className="sidebar" aria-label="Menu principal">
          <div className="sid-brand">
            <Brasao loja={loja} caixa="sid-gem" tamanho={20} />
            <div style={{ minWidth: 0 }}>
              <div className="sid-title">{nomeDaLoja(loja)}</div>
              <div className="sid-sub">
                {ehPlataforma(loja) ? (
                  'Administração da plataforma'
                ) : (
                  <>
                    {loja?.numero ? `Loja nº ${loja.numero}` : 'Portal administrativo'}
                    {loja?.oriente ? ` · ${loja.oriente}${loja.uf ? `/${loja.uf}` : ''}` : ''}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="nav">
            {secoes.map((secao) => (
              <div key={secao || 'topo'} className="nav-grupo">
                {secao && <div className="nav-sec">{secao}</div>}
                {permitidos
                  .filter((i) => i.secao === secao)
                  .map((item) => (
                    <NavLink
                      key={item.para}
                      to={item.para}
                      end={item.para === '/'}
                      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                      onMouseEnter={() => preparar(item.para)}
                      onFocus={() => preparar(item.para)}
                    >
                      <Icone nome={item.icone} tamanho={17} />
                      {item.rotulo}
                    </NavLink>
                  ))}
              </div>
            ))}
          </div>

          <div className="sid-footer">
            {/* Borrar nomes para projetar em sessão é de quem apresenta números
                ou o quadro — o irmão comum não projeta tela nenhuma. */}
            {(pode('tesouraria:ler') || pode('financeiro:resumo') || pode('sessoes:escrever') || pode('quadro:escrever')) && (
              <BotaoApresentacao />
            )}
            <div className="sid-user">
              {/* O próprio cadastro é do irmão: telefone, endereço, família e foto
                  ele mesmo atualiza, sem procurar o nome na lista do quadro. */}
              <button
                type="button"
                className="sid-user-abrir"
                onClick={() => setMeusDados(true)}
                disabled={meuMembro == null}
                title={meuMembro == null ? undefined : 'Meus dados'}
              >
                <AvatarDoUsuario nome={usuario?.nome_completo ?? ''} foto={usuario?.foto_url ?? null} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="sid-user-nome">{usuario?.nome_completo}</div>
                  <div className="sid-user-papel">{usuario?.papeis.join(', ')}</div>
                </div>
              </button>
              <button
                type="button"
                className="sid-sair"
                onClick={encerrar}
                title="Sair do portal"
                aria-label="Sair do portal"
              >
                <Icone nome="sair" tamanho={16} />
              </button>
            </div>
          </div>
        </nav>

        <main className="main">
          <div className="main-interno">
            <FaixaApresentacao />
            <AvisoDaAssinatura />
            <Suspense fallback={<Carregando altura={260} />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {meusDados && meuMembro != null && (
        <Suspense fallback={null}>
          <PerfilMembro membroId={meuMembro} abaInicial="cadastro" aoFechar={() => setMeusDados(false)} />
        </Suspense>
      )}

      {maisAberto && (
        <div className="mobile-overlay" onClick={() => setMaisAberto(false)} role="presentation" />
      )}
      <div id="mobile-mais" className={maisAberto ? 'aberto' : ''}>
        {/* As mesmas seções da barra lateral: solta numa grade só, a lista
            do tesoureiro virava vinte botões sem ordem reconhecível. */}
        {secoesSecundarias.map((secao) => (
          <div key={secao || 'topo'} className="mn-mais-secao">
            <div className="nav-sec">{secao || 'Geral'}</div>
            <div className="mn-mais-grade">
              {secundarios
                .filter((i) => i.secao === secao)
                .map((item) => (
                  <button
                    key={item.para}
                    type="button"
                    className="mn-mais-btn"
                    onClick={() => irPara(item.para)}
                    onTouchStart={() => preparar(item.para)}
                  >
                    <Icone nome={item.icone} tamanho={20} />
                    {item.rotulo}
                  </button>
                ))}
            </div>
          </div>
        ))}
        <div className="mn-mais-grade" style={{ marginTop: 12 }}>
          {meuMembro != null && (
            <button
              type="button"
              className="mn-mais-btn"
              onClick={() => {
                setMaisAberto(false);
                setMeusDados(true);
              }}
            >
              <Icone nome="ficha" tamanho={20} />
              Meus dados
            </button>
          )}
          <button type="button" className="mn-mais-btn" onClick={encerrar}>
            <Icone nome="sair" tamanho={20} />
            Sair
          </button>
        </div>
      </div>

      <nav id="mobile-nav">
        {principais.map((item) => (
          <NavLink
            key={item.para}
            to={item.para}
            end={item.para === '/'}
            className={({ isActive }) => `mn-btn${isActive ? ' ativo' : ''}`}
            onClick={() => setMaisAberto(false)}
            onTouchStart={() => preparar(item.para)}
          >
            <Icone nome={item.icone} tamanho={21} />
            {item.rotulo}
          </NavLink>
        ))}
        <button
          type="button"
          className={`mn-btn${maisAberto ? ' ativo' : ''}`}
          onClick={() => setMaisAberto((v) => !v)}
        >
          <Icone nome={maisAberto ? 'fechar' : 'menu'} tamanho={21} />
          Mais
        </button>
      </nav>
    </>
  );
}

/** O retrato de quem está logado; sem foto (ou se ela falhar), as iniciais. */
function AvatarDoUsuario({ nome, foto }: { nome: string; foto: string | null }) {
  const [falhou, setFalhou] = useState(false);
  useEffect(() => setFalhou(false), [foto]);
  if (foto && !falhou) {
    return (
      <div className="av av-foto">
        <img src={foto} alt="" decoding="async" onError={() => setFalhou(true)} />
      </div>
    );
  }
  return <div className="av">{iniciais(nome)}</div>;
}
